import assign from 'lodash/assign';

import I18N from 'assets/i18n/brandlift';
import brandliftTranslations from 'assets/i18n/brandlift-project';
import {BrandTypes, BrandliftContext} from 'client/common/store/reducers/context/types';

import {MetricMap} from 'client/common/types/brandlift/consts';
import {Metric, MetricInfo} from 'client/common/types/brandlift/types';
import {BrandliftLogo} from 'client/common/store/reducers/context/types';

const i18n = I18N.brandliftPage;

const NO_ANSWER = brandliftTranslations.common.utils.generatePreviewData.noAnswer;

export interface PreviewData {
    label: string;
    options: string[];
    term: MetricInfo['term'];
    metricName: Metric;
    hint?: string;
    placeholder: string;
    logos: BrandliftLogo[];
}

type PreviewDataGenerator = (
    context: BrandliftContext,
    metricLabel?: string,
    metricOptions?: string[],
    metricPlaceholder?: string,
) => Pick<PreviewData, 'label' | 'options' | 'placeholder'>;

type PreviewDataMap = {[metricName in Metric]: PreviewDataGenerator};

const PREVIEW_DATA_BY_METRICS: PreviewDataMap = {
    brandAwareness: (
        {brand = '', brands = ['', '', '', ''], isCustomFormulations, version},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        let label = '';

        if (typeof metricPlaceholder === 'string' || metricLabel === undefined) {
            switch (version) {
                case 3:
                    label = i18n.questionLabel2.brandAwareness_v3;
                    break;
                default:
                    label = i18n.questionLabel2.brandAwareness;
            }
        } else {
            label = metricLabel;
        }

        const placeholder =
            typeof metricPlaceholder === 'string' && isCustomFormulations
                ? metricPlaceholder
                : i18n.questionLabel2.productPlaceholder[1];
        const options = assign([brand, ...brands], metricOptions);

        return {
            label,
            options: options.concat(NO_ANSWER),
            placeholder
        };
    },
    adRecall: (
        {brand = '', brands = ['', '', '', ''], isCustomFormulations},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        const label =
            typeof metricPlaceholder === 'string' || metricLabel === undefined
                ? i18n.questionLabel2.adRecall
                : metricLabel;
        const placeholder =
            typeof metricPlaceholder === 'string' && isCustomFormulations
                ? metricPlaceholder
                : i18n.questionLabel2.productPlaceholder[2];
        const options = assign([brand, ...brands], metricOptions);

        return {
            label,
            options: options.concat(NO_ANSWER),
            placeholder
        };
    },
    adMessageRecall: (
        {adMessages = ['', '', '', ''], adMessage = [''], isCustomFormulations},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        const label =
            typeof metricPlaceholder === 'string' || metricLabel === undefined
                ? i18n.questionLabel2.adMessageRecall
                : metricLabel;
        const placeholder =
            typeof metricPlaceholder === 'string' && isCustomFormulations
                ? metricPlaceholder
                : i18n.questionLabel2.productPlaceholder[0];
        const options = Array.isArray(adMessage)
            ? adMessage.concat(adMessages)
            : [adMessage, ...adMessages];

        return {
            label,
            options: assign([], options, metricOptions).concat(NO_ANSWER),
            placeholder
        };
    },
    productConsideration: (
        {brand = '', brands = ['', '', '', ''], isCustomFormulations, version},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        let label = '';

        if (typeof metricPlaceholder === 'string' || metricLabel === undefined) {
            switch (version) {
                case 3:
                case 2:
                    label = i18n.questionLabel2.productConsideration2;
                    break;
                default:
                    label = i18n.questionLabel2.productConsideration;
            }
        } else {
            label = metricLabel;
        }

        let placeholder = '';

        if (typeof metricPlaceholder === 'string' && isCustomFormulations) {
            placeholder = metricPlaceholder;
        } else {
            switch (version) {
                case 3:
                case 2:
                    [,placeholder] = i18n.questionLabel2.productPlaceholder2;
                    break;
                default:
                    [,placeholder] = i18n.questionLabel2.productPlaceholder;
            }
        }

        const options = assign([brand, ...brands], metricOptions);

        return {
            label,
            options: options.concat(NO_ANSWER),
            placeholder
        };
    },
    brandFavorability: (
        {brand = '', brands = ['', '', '', ''], isCustomFormulations},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        const label =
            typeof metricPlaceholder === 'string' || metricLabel === undefined
                ? i18n.questionLabel2.brandFavorability
                : metricLabel;
        const placeholder =
            typeof metricPlaceholder === 'string' && isCustomFormulations
                ? metricPlaceholder
                : i18n.questionLabel2.productPlaceholder[1];
        const options = assign([brand, ...brands], metricOptions);

        return {
            label,
            options: options.concat(NO_ANSWER),
            placeholder
        };
    },
    purchaseIntent: (
        {brand = '', brands = ['', '', '', ''], brandType, isCustomFormulations},
        metricLabel,
        metricOptions,
        metricPlaceholder
    ) => {
        const defaultLabel = 
            brandType === BrandTypes.SERVICE
                ? i18n.questionLabel2.purchaseIntentForService
                : i18n.questionLabel2.purchaseIntentForProduct;

        const defaultPlaceholder =
            brandType === BrandTypes.SERVICE
                ? i18n.questionLabel2.servicePlaceholder
                : i18n.questionLabel2.productPlaceholder[0];

        const label =
            typeof metricPlaceholder === 'string' || metricLabel === undefined
                ? defaultLabel
                : metricLabel;
        const placeholder =
            typeof metricPlaceholder === 'string' && isCustomFormulations
                ? metricPlaceholder
                : defaultPlaceholder;
        const options = assign([brand, ...brands], metricOptions);

        return {
            label,
            options: options.concat(NO_ANSWER),
            placeholder
        };
    }
};

export const genPreviewData = (metricsList: Metric[], context: BrandliftContext): PreviewData[] => {
    return metricsList.map(metricName => {
        const metricData = PREVIEW_DATA_BY_METRICS[metricName](
            context,
            context.metricLabels[metricName],
            context.metricOptions[metricName],
            context.metricPlaceholders && context.metricPlaceholders[metricName]
        );

        return Object.assign(metricData, {
            term: MetricMap[metricName].term,
            metricName,
            hint: i18n.questionHint[metricName],
            logos: context.logos || []
        });
    });
};
